// src/composables/useEnvironment.js - Refactored with useAsyncState
//
// Scope: environment DETECTION + language init only.
//
// The Telegram SDK lifecycle deliberately does NOT live here — `TmaAdapter`
// (src/platform/tma/TmaAdapter.js) is its single owner (tma-viewport P1-A, D2).
// This file used to call `initializeTelegramSDK()` from src/services/telegram.js
// in two places (the async body and a legacy watcher), so the SDK was mounted
// and its CSS vars bound BEFORE the adapter ran. The adapter's own
// `viewport.bindCssVars()` then threw `CSSVarsBoundError` — sdk 3.9.2's
// `isAvailable()` does not check the bound state — which aborted the rest of
// the adapter's init: header colour, theme vars and the swipe guard never ran.
// Do not re-introduce an SDK call here.
import { useAsyncState } from '@vueuse/core';
import { onMounted, readonly, ref } from 'vue';
import { useLanguage } from './useLanguage.js';
import { isTelegramEnv } from '@/platform/index.js';

const currentEnvironment = ref(null); // 'pwa', 'tma', or 'web' (fallback)
const isInitialized = ref(false);
const isTelegram = ref(false);
const isPWA = ref(false);

export function useEnvironment() {
  /**
   * Detect environment synchronously
   * This should be called first, outside async context
   */
  const detectEnvironment = () => {
    // Check for Telegram environment first.
    // `isTelegramEnv()` from src/platform/index.js is the SINGLE detection
    // source (PWA audit MEDIUM-8 consolidated seven scattered signal sets into
    // it). The old local `window.telegramEnv || isTMA()` pair was a narrower
    // subset that could classify a Telegram session as PWA.
    if (isTelegramEnv()) {
      currentEnvironment.value = 'tma';
      isTelegram.value = true;
      isPWA.value = false;
      console.log('[Environment] Detected: Telegram Mini-App');
    } else {
      // Basic PWA check
      currentEnvironment.value = 'pwa';
      isTelegram.value = false;
      isPWA.value = true;
      console.log('[Environment] Detected: PWA/Web');
    }
  };

  /**
   * Initialize environment with useAsyncState
   * Non-blocking initialization pattern
   *
   * @returns {Object} AsyncState with state, isReady, isLoading, error
   */
  function initEnvironment() {
    // ═══════════════════════════════════════════════════════════
    // CRITICAL: Detect environment SYNCHRONOUSLY first
    // This must happen before any async work
    // ═══════════════════════════════════════════════════════════
    if (!currentEnvironment.value) {
      detectEnvironment();
    }

    if (isInitialized.value) {
      return {
        state: ref({ status: 'already-initialized' }),
        isReady: ref(true),
        isLoading: ref(false),
        error: ref(undefined),
      };
    }

    // ═══════════════════════════════════════════════════════════
    // Use useAsyncState for async initialization work
    // Returns refs immediately, updates when promise resolves
    // ═══════════════════════════════════════════════════════════
    return useAsyncState(
      async () => {
        console.log('[Environment] Starting async initialization...');

        const { initLanguage } = useLanguage();

        // Initialize language (handle if it returns a promise)
        const langResult = initLanguage();
        if (langResult instanceof Promise) {
          await langResult;
        }
        console.log('[Environment] Language initialized');

        // Platform-specific initialization belongs to the platform adapter
        // (adapter.initialize(), called right after this settles in App.vue).
        if (currentEnvironment.value === 'tma') {
          return { status: 'tma-ready', platform: 'telegram' };
        }
        return { status: 'pwa-ready', platform: 'pwa' };
      },
      { status: 'pending', platform: null }, // Initial state
      {
        immediate: true,
        resetOnExecute: false,
        onError: (error) => {
          console.error('[Environment] Initialization error:', error);
          // Still mark as initialized to prevent blocking
          isInitialized.value = true;
        },
        onSuccess: (result) => {
          console.log('[Environment] Initialization successful:', result);
          // Mark as initialized after successful completion
          isInitialized.value = true;
        },
      },
    );
  }

  onMounted(() => {
    // onMounted is not needed here since we init in App.vue
    // Keeping for backward compatibility
  });

  return {
    currentEnvironment: readonly(currentEnvironment),
    isTelegram: readonly(isTelegram),
    isPWA: readonly(isPWA),
    isInitialized: readonly(isInitialized),
    initEnvironment, // Now returns useAsyncState object
    detectEnvironment,
  };
}
